import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { Inject, Injectable } from '@angular/core';
import { FIREBASE_APP } from 'ag-common-svc/public-api';
import { FirebaseApp } from 'firebase/app';
import * as uuid from 'uuid';
import { Auth, getAuth } from 'firebase/auth';
import { collection, doc, DocumentData, Firestore, getFirestore, writeBatch } from 'firebase/firestore';

export enum BatchCommandType {
  set = 'set',
  update = 'update',
  remove = 'remove',
}

export enum BatchCommandKeys {
  type = 'type',
  dbId = 'dbId',
  table = 'table',
  data = 'data',
  toFirestore = 'toFirestore',
}

export interface BatchCommand<T = any> {
  [BatchCommandKeys.type]: BatchCommandType;
  [BatchCommandKeys.dbId]?: string;
  [BatchCommandKeys.table]: string;
  [BatchCommandKeys.data]?: T;
  [BatchCommandKeys.toFirestore]?: Function;
}

@Injectable({ providedIn: 'root' })
export class BatchOperationsService {
  readonly db: Firestore;
  private readonly auth: Auth;

  constructor(@Inject(FIREBASE_APP) fireBaseApp: FirebaseApp) {
    this.db = getFirestore(fireBaseApp);
    this.auth = getAuth(fireBaseApp);
  }

  async runBatchOperations(commands: BatchCommand[]) {
    const currentUser = this.auth?.currentUser;
    const idTokenResult = !!currentUser ? await currentUser?.getIdTokenResult() : null;
    const loggedInAgentDbId = idTokenResult?.claims?.agentDbId;
    const batchSize = 499;
    const batchCount = Math.ceil(commands.length / batchSize);

    for (let i = 0; i < batchCount; i++) {
      const startIndex = i * batchSize;
      const endIndex = (i + 1) * batchSize;
      const batchCommands = commands.slice(startIndex, endIndex);
      const batch = writeBatch(this.db);

      for (let command of batchCommands) {
        const dbId = command?.[BatchCommandKeys.dbId];
        const data = command?.[BatchCommandKeys.data] ?? {};
        const collectionReference = this.getCollectionReference({ loggedInAgentDbId, currentUser }, command);

        switch (command[BatchCommandKeys.type]) {
          case BatchCommandType.set:
            const addedDocRef = dbId ? doc(collectionReference, dbId) : doc(collectionReference);

            batch.set(addedDocRef, Object.assign({ [BaseModelKeys.dbId]: addedDocRef.id }, data));
            break;
          case BatchCommandType.update:
            if (!dbId) {
              console.error('Batch update command is missing dbId', command);
              break;
            }

            const updatedDoc = doc(collectionReference, dbId);

            batch.set(updatedDoc, data, { merge: true });
            break;
          case BatchCommandType.remove:
            // const removedDoc = doc(collectionReference, dbId);

            // // batch.set(removedDoc, {}, { merge: true });
            // batch.delete(removedDoc);
            break;
        }
      }

      await batch.commit();
    }
  }

  generateDbId = (command: BatchCommand) => {
    const dbId = command?.[BatchCommandKeys.dbId];

    if (dbId) {
      return dbId;
    }
    const table = command?.[BatchCommandKeys.table];

    return this.generateTableDocDbId(table);
  };

  generateTableDocDbId = (table: string) => {
    const collectionReference = collection(this.db, table);
    const document = doc(collectionReference);

    return document?.id;
  };

  private getCollectionReference = ({ currentUser, loggedInAgentDbId }, { type, table, toFirestore }: BatchCommand) => {
    switch (type) {
      case BatchCommandType.set:
        return collection(this.db, table).withConverter({
          fromFirestore: null,
          toFirestore: (item, ...rest): DocumentData => {
            return Object.assign(
              {
                [BaseModelKeys.actionId]: uuid.v4(),
                [BaseModelKeys.createdDate]: new Date(),
                [BaseModelKeys.createdBy]: currentUser?.uid ?? null,
                [BaseModelKeys.createdByEmail]: currentUser?.email ?? item?.created_by ?? null,
                [BaseModelKeys.createdByAgentDbId]: loggedInAgentDbId ?? null,
              },
              toFirestore ? toFirestore(item) : item,
            );
          },
        });
      case BatchCommandType.update:
        return collection(this.db, table).withConverter({
          fromFirestore: null,
          toFirestore: (item, ...rest): DocumentData => {
            return Object.assign(
              {
                [BaseModelKeys.actionId]: uuid.v4(),
                [BaseModelKeys.updatedDate]: new Date(),
                [BaseModelKeys.updatedBy]: currentUser?.uid ?? null,
                [BaseModelKeys.updatedByEmail]: currentUser?.email ?? null,
                [BaseModelKeys.updatedByAgentDbId]: loggedInAgentDbId ?? null,
              },
              toFirestore ? toFirestore(item) : item,
            );
          },
        });
      case BatchCommandType.remove:
        return collection(this.db, table).withConverter({
          fromFirestore: null,
          toFirestore: (item, ...rest): DocumentData => {
            return Object.assign(
              {
                [BaseModelKeys.actionId]: uuid.v4(),
                [BaseModelKeys.deletedDate]: new Date(),
                [BaseModelKeys.deletedBy]: currentUser?.uid ?? null,
                [BaseModelKeys.deletedByEmail]: currentUser?.email ?? null,
                [BaseModelKeys.deletedByAgentDbId]: loggedInAgentDbId ?? null,
              },
              item,
            );
          },
        });
    }
  };
}
